import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { apiFetch } from '../lib/api.ts';

interface Excluded { productId: string; name: string | null; brand: string | null; excludedAt: string; }
interface SearchHit { productId: string; name: string; brand: string | null; excluded: boolean; }
interface BulkResult { added: string[]; alreadyExcluded: string[]; notFound: string[]; }

/** Build a CSV template the user fills in Excel and re-uploads. */
function downloadTemplate(): void {
  const csv = 'sku,note\r\nEXAMPLE-SKU-001,optional note (ignored)\r\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'rfid-exclusions-template.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Read an uploaded .csv or .xlsx and return the SKUs in the first column. */
async function parseSkuFile(file: File): Promise<string[]> {
  let rows: string[][];
  if (file.name.toLowerCase().endsWith('.xlsx')) {
    // Lazy-load the xlsx reader (browser build) so it only ships when needed.
    // readSheet() returns the first sheet's rows (SheetData = Row[]).
    const { readSheet } = await import('read-excel-file/browser');
    const data = await readSheet(file);
    rows = data.map((r) => r.map((c) => (c == null ? '' : String(c))));
  } else {
    const text = await file.text();
    rows = text.split(/\r?\n/).map((line) => line.split(','));
  }
  const skus: string[] = [];
  rows.forEach((row, i) => {
    const first = (row[0] ?? '').trim();
    if (!first) return;
    if (i === 0 && /^(sku|product[_ ]?id)$/i.test(first)) return; // skip header row
    skus.push(first);
  });
  return skus;
}

export function RfidExclusionsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const list = useQuery({
    queryKey: ['rfid-exclusions'],
    queryFn: () => apiFetch<{ rows: Excluded[] }>('/api/admin/rfid-exclusions'),
  });
  const search = useQuery({
    queryKey: ['rfid-exclusions', 'search', q],
    queryFn: () => apiFetch<{ rows: SearchHit[] }>(`/api/admin/rfid-exclusions/search?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length > 0,
  });
  const add = useMutation({
    mutationFn: (productId: string) => apiFetch('/api/admin/rfid-exclusions', { method: 'POST', body: JSON.stringify({ productId }) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['rfid-exclusions'] }); },
  });
  const remove = useMutation({
    mutationFn: (productId: string) => apiFetch(`/api/admin/rfid-exclusions/${encodeURIComponent(productId)}`, { method: 'DELETE' }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['rfid-exclusions'] }); },
  });
  const bulk = useMutation({
    mutationFn: (productIds: string[]) =>
      apiFetch<BulkResult>('/api/admin/rfid-exclusions/bulk', { method: 'POST', body: JSON.stringify({ productIds }) }),
    onSuccess: (r) => { setBulkResult(r); void qc.invalidateQueries({ queryKey: ['rfid-exclusions'] }); },
  });

  async function onFile(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    if (file) {
      setBulkResult(null);
      setBulkError(null);
      try {
        const skus = await parseSkuFile(file);
        if (skus.length === 0) setBulkError('No SKUs found in the file (first column should be the SKU).');
        else bulk.mutate(skus);
      } catch (err) {
        setBulkError(`Could not read the file: ${(err as Error).message}`);
      }
    }
    if (fileRef.current) fileRef.current.value = ''; // allow re-uploading the same file
  }

  return (
    <div>
      <h1>RFID Exclusions</h1>
      <p>SKUs listed here are not RFID-tagged: they are left out of SmartLink orders but still written to OBM at their ordered quantity.</p>

      <h2>Bulk add from a spreadsheet</h2>
      <p>Download the template, put one SKU per row in the <code>sku</code> column, then upload it back (<code>.csv</code> or <code>.xlsx</code>). Unknown or already-excluded SKUs are reported and skipped.</p>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button onClick={downloadTemplate}>Download template</button>
        <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={onFile} disabled={bulk.isPending} />
        {bulk.isPending && <span>Uploading…</span>}
      </div>
      {bulkError && <p style={{ color: 'var(--danger, #d66)' }}>{bulkError}</p>}
      {bulkResult && (
        <div style={{ marginTop: 8 }}>
          <p>
            <strong>{bulkResult.added.length}</strong> added ·{' '}
            <strong>{bulkResult.alreadyExcluded.length}</strong> already excluded ·{' '}
            <strong>{bulkResult.notFound.length}</strong> not found
          </p>
          {bulkResult.notFound.length > 0 && (
            <p style={{ color: 'var(--danger, #d66)' }}>
              Not found (skipped): <span className="mono">{bulkResult.notFound.join(', ')}</span>
            </p>
          )}
        </div>
      )}

      <h2>Add a SKU</h2>
      <input placeholder="Search SKU or name" value={q} onChange={(e) => setQ(e.target.value)} />
      {search.data && (
        <table>
          <tbody>
            {search.data.rows.map((r) => (
              <tr key={r.productId}>
                <td>{r.productId}</td><td>{r.name}</td><td>{r.brand}</td>
                <td>
                  <button disabled={r.excluded || add.isPending} onClick={() => add.mutate(r.productId)}>
                    {r.excluded ? 'Excluded' : 'Exclude'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Excluded SKUs</h2>
      {list.isLoading ? <p>Loading…</p> : (
        <table>
          <thead><tr><th>SKU</th><th>Name</th><th>Brand</th><th>Excluded at</th><th></th></tr></thead>
          <tbody>
            {(list.data?.rows ?? []).map((r) => (
              <tr key={r.productId}>
                <td>{r.productId}</td><td>{r.name}</td><td>{r.brand}</td><td>{r.excludedAt}</td>
                <td><button disabled={remove.isPending} onClick={() => remove.mutate(r.productId)}>Re-include</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
